






/**
 * Allows to make search request using the TomTom's
 * [Search API - Geometry Search](https://developer.tomtom.com/search-api/search-api-documentation-search/geometry-search).
 *
 * Parameters can be passed to the constructor or provided via convenient methods that can be chained until
 * the method {{#crossLink "GeometrySearch/go:method"}}{{/crossLink}} performs the actual call.
 *
 * The call is asynchronous therefore the user have two options for receive the response:
 *  - Passing a callback function.
 *  - Use the {{#crossLink "Promise"}}{{/crossLink}} returned by the
 *    {{#crossLink "GeometrySearch/go:method"}}{{/crossLink}} method to handle the response.
 *
 * @example
 * ```js
 * var geometryList = [
 *   {
 *     type: "POLYGON",
 *     vertices: [
 *       "37.7524152343544,-122.43576049804686",
 *       "37.70660472542312,-122.43301391601562",
 *       "37.712059855877314,-122.36434936523438",
 *       "37.75350561243041,-122.37396240234374"
 *     ]
 *   },
 *   {
 *     type: "CIRCLE",
 *     position: "37.71205,-121.36434",
 *     radius: 6000
 *   },
 *   {
 *     type: "CIRCLE",
 *     position: "37.31205,-121.36434",
 *     radius: 1000
 *   }
 * ];
 *
 * function callbackFn(results) {
 *   console.log(results);
 * }
 *
 * // Using a callback function
 * tomtom.geometrySearch()
 *   .key(<Your API key>) // this is optional if you defined a global key earlier
 *   .geometryList(geometryList)
 *   .query('pizza')
 *   .callback(callbackFn)
 *   .go();
 *
 * // Or using a Promise
 * tomtom.geometrySearch()
 *   .key(<Your API key>) // this is optional if you defined a global key earlier
 *   .geometryList(geometryList)
 *   .query('pizza')
 *   .go()
 *   .then(callbackFn);
 * ```
 *
 * The SDK provides a {{#crossLink "Promise"}}{{/crossLink}} polyfill for browsers (IE<9)
 * without native ECMAScript 6 support.
 *
 * @class GeometrySearch
 * @extends Search
 * @uses QueryMixin
 * @uses IdxSetMixin
 * @uses ExtendedPostalCodesForMixin
 * @uses ViewMixin
 * @constructor
 * @module Services
 *
 * @param {Object}   [options] Options to be passed to the geometry search call,
 *     or Array of such options objects to perform batch request.
 * @param {String}   [options.key] An API Key issued from <a href="https://developer.tomtom.com">Developer Portal</a>
 * @param {"http"|"https"} [options.protocol="https"] Protocol's type
 * @param {Boolean}  [options.unwrapBbox=false] Changes the bounding box coordinates allowing longitudes that
 *  exceeds 180 degrees when is necessary. {{#crossLink "Search/unwrapBbox:method"}}More{{/crossLink}}
 * @param {Number}   [options.limit=10] Maximum number of responses that will be returned
 * @param {String}   [options.language=null] Language to use in the response.
 *  The list with all the available codes can be found
 *  <a href="https://developer.tomtom.com/search-api/search-api-documentation/supported-languages">here</a>.
 * @param {Function} [options.callback] Callback function to use in case of success
 * @param {Function} [options.fail=default] Callback function to use in case of failure
 * @param {String}   options.query Text that will be searched.
 *  This parameter is required to be defined either in the options passed to the constructor or using the
 *  **query()** method.
 * @param {Array}    [options.geometryList] Represents the list of geometries for the geometry search
 * @param {String}   [options.idxSet] Specify which indexes to utilize for the search
 * @param {String}   [options.extendedPostalCodesFor] Represent the indexes for which extended postal codes should
 *  be included in the results
 * @param {String}   [options.view] Sets the geopolitical view used in the service
 */

define(['./search', '../validators', '../converters', 'common/parameterApplications'],
    function(search, validators, converters, parameterApplications) {
        'use strict';

        var searchPrototype = search.prototype.constructor;

        function GeometrySearch(options) {
            this.options = options;
            this.fields = {};

            this.fields.type = {
                defaultValue: this.TYPE.geometry,
                visible: false,
                application: parameterApplications.PATH
            };

            this.fields.key = {
                validator: validators.string
            };

            this.fields.query = {
                validator: validators.string,
                required: true,
                application: parameterApplications.PATH
            };

            this.fields.limit = {
                validator: validators.limit
            };

            this.fields.language = {
                validator: validators.languageCode
            };

            this.fields.view = {
                validator: validators.geopolView('search', 'view')
            };

            /**
         * Sets or gets the current value of the option "geometryList".
         *
         * Its a list of geometries to search in.
         *
         * Currently only two types of geometries are being supported:
         *  - Polygons: Groups of coordinates that describes an enclosed area
         *  - Circles: Describe a circular area using an unique coordinate and a radius in meters
         *
         * @example
         * ```js
         * // A polygon area
         * [
         *   {
         *     type: "POLYGON",
         *     vertices: [
         *       "37.7524152343544,-122.43576049804686",
         *       "37.70660472542312,-122.43301391601562",
         *       "37.712059855877314,-122.36434936523438",
         *       "37.75350561243041,-122.37396240234374"
         *     ]
         *   }
         * ];
         * ```
         *
         * @example
         * ```js
         * // A couple of circle areas
         * var geometryList = [
         *   {
         *     type: "CIRCLE",
         *     position: "37.71205,-121.36434",
         *     radius: 6000 // Unit is in meters
         *   },
         *   {
         *     type: "CIRCLE",
         *     position: "37.31205,-121.36434",
         *     radius: 1000 // Unit is in meters
         *   }
         * ];
         * ```
         *
         * @example
         * ```js
         * // Mixing polygons and circles areas
         * var geometryList = [
         *   {
         *     type: "POLYGON",
         *     vertices: [
         *       "37.7524152343544,-122.43576049804686",
         *       "37.70660472542312,-122.43301391601562",
         *       "37.712059855877314,-122.36434936523438",
         *       "37.75350561243041,-122.37396240234374"
         *     ]
         *   },
         *   {
         *     type: "CIRCLE",
         *     position: "37.71205,-121.36434",
         *     radius: 6000
         *   },
         *   {
         *     type: "CIRCLE",
         *     position: "37.31205,-121.36434",
         *     radius: 1000
         *   }
         * ];
         * ```
         *
         * @method geometryList
         * @chainable
         * @param {Object[]} [newValue] List of geometries
         * @throws {TypeError} If the given argument cannot be converted to a valid value
         * @return {Object[]} The same service instance or the current option value if no argument was given
         */
            this.fields.geometryList = {
                validator: validators.geometryList,
                converter: converters.geometryList,
                application: parameterApplications.POST
            };

            this.fields.idxSet = {
                validator: validators.string
            };

            this.fields.protocol = {validator: validators.protocol};

            this.fields.extendedPostalCodesFor = { validator: validators.string };

            this.fields.unwrapBbox = {validator: validators.bool, defaultValue: false, required: true};
        }

        GeometrySearch.prototype = searchPrototype.prototype;
        GeometrySearch.prototype.constructor = GeometrySearch;

        return function(options) {
            return (new GeometrySearch(options)).construct();
        };
    });



// WEBPACK FOOTER //
// ./services/search/geometrySearch.js